import {
  AddComment,
  CheckmarkFilled,
  CheckmarkOutline,
  Close,
  CloseFilled,
  CloseOutline,
} from '@carbon/react/icons';
import { getFormValuesAtom } from 'atoms/form-atoms';
import { Button, Modal, DefinitionTooltip } from '@carbon/react';
import { TextAreaField } from 'components/form-fields/text-area-field';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'components/hover-card';
import { useCommentFormAtom } from 'components/json-renderer/atoms';
import { snakeToCapitalCase } from 'components/json-renderer/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useComponentVisible } from 'lib/hooks/useComponentVisible';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { capitalizeFirstLetter } from 'utils/strings';
import { FormattedCount } from 'components/formatted-count/formatted-count';
import {
  createVerificationItemEventsAtom,
  verificationItemCommentMutationAtom,
  verificationItemEventMutationAtom,
} from './atoms';
import styles from './verification-controls.module.scss';
import { VerificationEventsList } from './verification-events-list';

export interface VerificationControlsProps {
  path?: string[];
  eventsHref: string;
  commentsHref: string;
  commentTitle?: string;
  commentHelperText?: string;
  displayName?: string;
  className?: string;
  initialStatus?: boolean | null;
  initialCommentsCount?: number;
  skipModal?: boolean;
  disableActions?: boolean;
  hideComments?: boolean;
  onStatusChange?: (status: boolean | null) => void;
  onCommentsCountChange?: (count: number) => void;
}

export const VerificationControls: React.FC<VerificationControlsProps> = ({
  path,
  eventsHref,
  commentsHref,
  commentTitle = 'No comments added',
  commentHelperText = 'Add a comment for yourself or leave a note for others.',
  displayName,
  className,
  initialStatus,
  initialCommentsCount = 0,
  skipModal = false,
  disableActions = false,
  hideComments = false,
  onStatusChange,
  onCommentsCountChange,
}) => {
  const portalRef = useRef<HTMLDivElement>(null);
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false, false, portalRef);
  const eventsAtom = useMemo(
    () => createVerificationItemEventsAtom(eventsHref),
    [eventsHref]
  );
  const [eventMutationState, eventMutation] = useAtom(
    verificationItemEventMutationAtom
  );
  const [commentMutationState, commentMutation] = useAtom(
    verificationItemCommentMutationAtom
  );
  const fetchEvents = useSetAtom(eventsAtom);

  const nodeName = path && path.length > 0 ? path.slice(-1).pop() || '' : '';
  const terminalNode = displayName || snakeToCapitalCase(nodeName);

  // Store status in state - true = correct, false = incorrect, null = nothing checked
  const [status, setStatus] = useState<boolean | null>(initialStatus ?? null);

  const loading = eventMutationState.loading || commentMutationState.loading;

  // Track comments count - start with initialCommentsCount, update when events are fetched
  const [commentsCount, setCommentsCount] = useState(initialCommentsCount);

  // Callback to update comments count when events are loaded
  const handleEventsLoaded = useCallback(
    (count: number) => {
      setCommentsCount(count);
      onCommentsCountChange?.(count);
    },
    [onCommentsCountChange, setCommentsCount]
  );

  // Callback to refresh events after a comment is added
  const handleCommentAdded = useCallback(() => {
    fetchEvents();
  }, [fetchEvents]);

  const onChange = useCallback(
    (newStatus: boolean | null) => {
      const kind =
        newStatus === null
          ? 'verification_reverted'
          : newStatus
            ? 'verification_correct'
            : 'verification_incorrect';

      eventMutation({
        url: eventsHref,
        type: 'POST',
        data: {
          kind: kind as
            | 'verification_correct'
            | 'verification_incorrect'
            | 'verification_reverted',
          reason: null,
          details: null,
        },
        onSuccess: () => {
          // Only update status state if API call succeeds
          setStatus(newStatus);
          onStatusChange?.(newStatus);
        },
      });
    },
    [eventsHref, eventMutation, onStatusChange]
  );

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    currentState: 'correct' | 'incorrect';
    targetStatus: boolean | null;
  }>({
    isOpen: false,
    currentState: 'correct',
    targetStatus: null,
  });

  const handleConfirmRemove = useCallback(
    (comment?: string) => {
      // Use targetStatus from modal state to determine the action
      onChange(confirmModal.targetStatus);
      if (comment) {
        commentMutation({
          url: commentsHref,
          type: 'POST',
          data: {
            content: comment,
          },
          onSuccess: () => {
            fetchEvents();
          },
        });
      }
    },
    [
      onChange,
      commentsHref,
      commentMutation,
      fetchEvents,
      confirmModal.targetStatus,
    ]
  );

  return (
    <>
      <div className={`${styles.container} ${className || ''}`}>
        <div
          className={
            status === false || status === true
              ? styles.statusButtonsOn
              : styles.statusButtons
          }
        >
          <Button
            size="sm"
            hasIconOnly
            kind="ghost"
            iconDescription={
              status === false ? 'Remove Incorrect Status' : 'Mark as Incorrect'
            }
            tooltipAlignment="center"
            tooltipPosition="top"
            renderIcon={status === false ? CloseFilled : CloseOutline}
            className={status === false ? styles.notApprovedBtn : ''}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              if (skipModal) {
                onChange(status === false ? null : false);
              } else if (status === false) {
                setConfirmModal({
                  isOpen: true,
                  currentState: 'incorrect',
                  targetStatus: null,
                });
              } else {
                onChange(false);
              }
            }}
            disabled={loading || disableActions}
          />
          <Button
            size="sm"
            hasIconOnly
            kind="ghost"
            iconDescription={
              status === true ? 'Remove Correct Status' : 'Mark as Correct'
            }
            tooltipAlignment="center"
            tooltipPosition="top"
            renderIcon={status === true ? CheckmarkFilled : CheckmarkOutline}
            className={status === true ? styles.approvedBtn : ''}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              if (skipModal) {
                onChange(status === true ? null : true);
              } else if (status === false) {
                setConfirmModal({
                  isOpen: true,
                  currentState: 'incorrect', // Removing incorrect status to mark as correct
                  targetStatus: true, // Change from incorrect to correct
                });
              } else if (status === true) {
                setConfirmModal({
                  isOpen: true,
                  currentState: 'correct',
                  targetStatus: null, // Remove correct status
                });
              } else {
                onChange(true);
              }
            }}
            disabled={loading || disableActions}
          />
        </div>
        {!hideComments && (
          <div ref={ref}>
            <HoverCard open>
              {isComponentVisible && (
                <HoverCardContent
                  align="center"
                  side="bottom"
                  className={styles.commentsHoverCard}
                  usePortal
                  onPointerDownOutside={(e) => e.preventDefault()}
                  onInteractOutside={(e) => e.preventDefault()}
                >
                  <div ref={portalRef} onMouseDown={(e) => e.stopPropagation()}>
                    <div className={styles.closeButtonArea}>
                      <span
                        dangerouslySetInnerHTML={{ __html: terminalNode }}
                      />
                      <Button
                        kind="ghost"
                        hasIconOnly
                        iconDescription="Close"
                        size="md"
                        onClick={() =>
                          setIsComponentVisible((current) => !current)
                        }
                        renderIcon={Close}
                        className={styles.closeButton}
                        tooltipAlignment="end"
                        tooltipPosition="left"
                      />
                    </div>
                    <VerificationEventsList
                      title={commentTitle}
                      helperText={commentHelperText}
                      eventsHref={eventsHref}
                      commentsHref={commentsHref}
                      onComment={handleCommentAdded}
                      onEventsLoaded={handleEventsLoaded}
                    />
                  </div>
                </HoverCardContent>
              )}
              <HoverCardTrigger asChild>
                <span
                  className={`${
                    commentsCount > 0 ? styles.activeComments : ''
                  } ${disableActions ? styles.disabledComment : ''}`}
                  onClick={() => {
                    if (!disableActions) {
                      setIsComponentVisible((current) => !current);
                    }
                  }}
                >
                  <DefinitionTooltip
                    align="top"
                    definition="Add Comment"
                    className={styles.commentTooltip}
                    openOnHover
                  >
                    <AddComment />
                    {commentsCount > 0 ? (
                      <FormattedCount value={commentsCount} />
                    ) : (
                      ''
                    )}
                  </DefinitionTooltip>
                </span>
              </HoverCardTrigger>
            </HoverCard>
          </div>
        )}
      </div>
      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        currentState={confirmModal.currentState}
        terminalNode={terminalNode}
        onConfirm={handleConfirmRemove}
        onClose={() =>
          setConfirmModal({
            isOpen: false,
            currentState: 'correct',
            targetStatus: null,
          })
        }
      />
    </>
  );
};

// Modals
interface ConfirmActionModalProps {
  isOpen: boolean;
  currentState: 'correct' | 'incorrect';
  terminalNode: string;
  onConfirm: (comment?: string) => void;
  onClose: () => void;
}

const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  isOpen,
  currentState,
  terminalNode,
  onConfirm,
  onClose,
}) => {
  const confirmActionMessage = capitalizeFirstLetter(currentState);
  const formAtom = useCommentFormAtom(isOpen);
  const form = useAtomValue(formAtom);
  const formValuesAtom = React.useMemo(
    () => getFormValuesAtom(formAtom),
    [formAtom]
  );
  const formValues = useAtomValue(formValuesAtom);

  return (
    <Modal
      size="xs"
      modalHeading={`Remove ${confirmActionMessage} Status?`}
      danger
      secondaryButtonText="Cancel"
      primaryButtonText="Remove"
      className={styles.confirmActionModal}
      open={isOpen}
      onRequestClose={onClose}
      onRequestSubmit={() => {
        onConfirm(formValues.comment ? formValues.comment : undefined);
        onClose();
      }}
    >
      <p>
        This will remove the {confirmActionMessage} status for &quot;
        {capitalizeFirstLetter(terminalNode)}&quot;.
      </p>
      {currentState === 'incorrect' && (
        <fieldset>
          <TextAreaField
            id="incorrect-comment"
            labelText="Add Comment (optional)"
            fieldAtom={form.comment}
          />
        </fieldset>
      )}
    </Modal>
  );
};
